// Kid-friendly category-wise comparison across the last 2–6 months.
// Shows a grouped bar chart + simple verdict cards with smileys.
import React, { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend, Cell,
} from "recharts";
import { Card } from "./primitives";
import { CATEGORY_COLORS, fmtCAD, fmtCADCompact, monthShort } from "../lib/budget";

const RANGE_OPTIONS = [2, 3, 4, 5, 6];

// Friendly month-over-month colour palette (gentle gradient)
const MONTH_BAR_COLORS = ["#5F7161", "#8FB9AA", "#E2B35B", "#E27D60", "#B56B7E", "#6E7A91"];

const TICK_11 = { fontSize: 11 };
const TICK_10 = { fontSize: 10 };
const LEGEND_STYLE = { fontSize: 12, paddingTop: 8 };
const BAR_MARGIN = { top: 16, right: 8, left: 0, bottom: 36 };

function buildVerdict(category, latestVal, previousVals) {
  // previousVals is the array of previous months (oldest -> newest, excluding latest)
  const sum = previousVals.reduce((s, v) => s + (Number(v) || 0), 0);
  const avg = previousVals.length > 0 ? sum / previousVals.length : 0;
  const diff = latestVal - avg;
  const pct = avg > 0 ? (diff / avg) * 100 : (latestVal > 0 ? 100 : 0);

  let mood = "same";
  if (Math.abs(pct) < 2.5) mood = "same";
  else if (pct < 0) mood = "less"; // good
  else mood = "more"; // bad

  return { category, latestVal, avg, diff: Math.round(diff), pct: Math.round(pct), mood };
}

const MOOD_META = {
  less: { emoji: "🎉", bg: "bg-[#E6F0E0]", border: "border-[#C6D9BD]", chip: "bg-[#5F7161] text-white", text: "text-[#1A1C1A]", verb: "less" },
  more: { emoji: "⚠️", bg: "bg-[#FCEDE6]", border: "border-[#F0C6B5]", chip: "bg-[#E27D60] text-white", text: "text-[#1A1C1A]", verb: "more" },
  same: { emoji: "😐", bg: "bg-[#F4F2EC]", border: "border-[#E0DCD2]", chip: "bg-[#8A8C88] text-white", text: "text-[#1A1C1A]", verb: "same" },
};

function buildMessage(v, range) {
  if (v.latestVal === 0 && v.avg === 0) {
    return `No spending on ${v.category}. 👍`;
  }
  const compareWord = range === 2 ? "last month" : `the last ${range - 1} months`;
  if (v.mood === "less") {
    return `Yay! You spent ${Math.abs(v.pct)}% less on ${v.category} than ${compareWord}. Keep it up!`;
  }
  if (v.mood === "more") {
    return `Heads up — you spent ${v.pct}% more on ${v.category} than ${compareWord}. Try to cut back next month!`;
  }
  return `About the same on ${v.category} as ${compareWord}. Steady as she goes.`;
}

export default function MonthlyComparison({ allBudgets, liveSnapshot, categories }) {
  const [range, setRange] = useState(3);

  // Combine saved budgets with the in-progress liveSnapshot (overrides the same month)
  const merged = useMemo(() => {
    const map = new Map();
    (allBudgets || []).forEach((b) => {
      map.set(b.month, { month: b.month, expenses: b.expenses || {} });
    });
    if (liveSnapshot && liveSnapshot.month) {
      map.set(liveSnapshot.month, { month: liveSnapshot.month, expenses: liveSnapshot.expenses || {} });
    }
    return Array.from(map.values()).sort((a, b) => a.month.localeCompare(b.month));
  }, [allBudgets, liveSnapshot]);

  // Pick the last `range` months
  const months = useMemo(() => merged.slice(-range), [merged, range]);  const chartData = useMemo(() => {
    return (categories || []).map((cat) => {
      const row = { category: cat, shortCategory: cat.length > 12 ? `${cat.slice(0, 11)}…` : cat };
      months.forEach((m) => {
        row[m.month] = Math.round((Number(m.expenses[cat]) || 0) * 100) / 100;
      });
      return row;
    });
  }, [categories, months]);

  const verdicts = useMemo(() => {
    if (months.length < 2) return [];
    const latestMonth = months[months.length - 1];
    const previousMonths = months.slice(0, -1);
    return (categories || [])
      .map((cat) => {
        const latestVal = Number(latestMonth.expenses[cat]) || 0;
        const previousVals = previousMonths.map((m) => Number(m.expenses[cat]) || 0);
        return buildVerdict(cat, latestVal, previousVals);
      })
      // Sort biggest absolute change first so the most impactful insights are on top
      .sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct));
  }, [categories, months]);

  if (months.length < 2) {
    return (
      <Card className="p-6 sm:p-8" testId="card-comparison" delay={0}>
        <SectionHeader range={range} setRange={setRange} availableMonths={merged.length} />
        <div className="text-center py-10 text-sm text-[#5A5C58]">
          You need at least 2 saved months to compare. Save another month&apos;s budget and come back! 📅
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 sm:p-8" testId="card-comparison" delay={0}>
      <SectionHeader range={range} setRange={setRange} availableMonths={merged.length} />

      <div className="h-[340px]" data-testid="comparison-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={BAR_MARGIN} barCategoryGap="18%">
            <CartesianGrid stroke="#EFEDE7" vertical={false} />
            <XAxis
              dataKey="shortCategory"
              stroke="#5A5C58"
              tick={TICK_10}
              axisLine={false}
              tickLine={false}
              angle={-22}
              textAnchor="end"
              interval={0}
              height={48}
            />
            <YAxis stroke="#5A5C58" tick={TICK_11} axisLine={false} tickLine={false} tickFormatter={fmtCADCompact} />
            <RTooltip
              formatter={(v) => fmtCAD(v)}
              labelFormatter={(label, payload) => (payload && payload[0] ? payload[0].payload.category : label)}
              contentStyle={{ borderRadius: 12, border: "1px solid #D8D6D0" }}
            />
            <Legend wrapperStyle={LEGEND_STYLE} />
            {months.map((m, idx) => (
              <Bar
                key={m.month}
                dataKey={m.month}
                name={monthShort(m.month)}
                radius={[6, 6, 0, 0]}
                fill={MONTH_BAR_COLORS[idx % MONTH_BAR_COLORS.length]}
              >
                {chartData.map((row) => (
                  <Cell key={`${m.month}-${row.category}`} fill={MONTH_BAR_COLORS[idx % MONTH_BAR_COLORS.length]} />
                ))}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <PocketMoneyCallout verdicts={verdicts} range={range} />

      <Verdicts verdicts={verdicts} range={range} />
    </Card>
  );
}

const SectionHeader = ({ range, setRange, availableMonths }) => (
  <div className="mb-5">
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-2">
      <div>
        <h3 className="font-display text-xl font-semibold">Month-by-Month Compare 📊</h3>
        <p className="text-sm text-[#5A5C58]">
          Comparing your <span className="font-medium">variable spending</span> — the things you can actually control. Pick how many months to compare!
        </p>
      </div>
      <div className="flex items-center gap-1 bg-[#F4F2EC] rounded-full p-1 border border-[#E0DCD2]" data-testid="comparison-range-picker">
        {RANGE_OPTIONS.map((n) => {
          const disabled = n > Math.max(2, availableMonths);
          const active = range === n;
          return (
            <button
              key={n}
              data-testid={`comparison-range-${n}`}
              onClick={() => !disabled && setRange(n)}
              disabled={disabled}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                active ? "bg-[#5F7161] text-white shadow-sm" :
                disabled ? "text-[#8A8C88]/60 cursor-not-allowed" :
                "text-[#5A5C58] hover:bg-white"
              }`}
              title={disabled ? "Save more months first" : `Compare ${n} months`}
            >
              {n} mo
            </button>
          );
        })}
      </div>
    </div>
  </div>
);

const Verdicts = ({ verdicts, range }) => {
  const summary = useMemo(() => {
    let up = 0, down = 0, same = 0;
    verdicts.forEach((v) => {
      if (v.mood === "more") up += 1;
      else if (v.mood === "less") down += 1;
      else same += 1;
    });
    return { up, down, same };
  }, [verdicts]);

  return (
    <div className="mt-6" data-testid="comparison-verdicts">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h4 className="font-display text-base font-semibold">How did you do?</h4>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-1 rounded-full bg-[#E6F0E0] text-[#3F5234] font-medium">🎉 {summary.down} better</span>
          <span className="px-2 py-1 rounded-full bg-[#F4F2EC] text-[#5A5C58] font-medium">😐 {summary.same} same</span>
          <span className="px-2 py-1 rounded-full bg-[#FCEDE6] text-[#8A4030] font-medium">⚠️ {summary.up} more</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {verdicts.map((v) => {
          const meta = MOOD_META[v.mood];
          return (
            <div
              key={v.category}
              data-testid={`verdict-${v.category}`}
              className={`rounded-xl border ${meta.border} ${meta.bg} p-3.5 flex items-start gap-3`}
            >
              <span className="text-2xl shrink-0" aria-hidden="true">{meta.emoji}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: CATEGORY_COLORS[v.category] }} />
                  <span className="font-medium text-sm truncate">{v.category}</span>
                  <span className={`ml-auto text-[11px] font-mono px-2 py-0.5 rounded-full ${meta.chip} shrink-0`}>
                    {v.mood === "same" ? "≈ 0%" : `${v.pct > 0 ? "+" : ""}${v.pct}%`}
                  </span>
                </div>
                <p className="text-xs text-[#1A1C1A] leading-snug">{buildMessage(v, range)}</p>
                <p className="text-[10px] text-[#5A5C58] mt-1 font-mono">
                  This month {fmtCAD(v.latestVal)} · Avg before {fmtCAD(v.avg)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PocketMoneyCallout = ({ verdicts, range }) => {
  const saved = verdicts.filter((v) => v.mood === "less").reduce((s, v) => s + (v.avg - v.latestVal), 0);
  const extra = verdicts.filter((v) => v.mood === "more").reduce((s, v) => s + (v.latestVal - v.avg), 0);
  const net = Math.round((saved - extra) * 100) / 100;
  const savedRounded = Math.round(saved * 100) / 100;
  const extraRounded = Math.round(extra * 100) / 100;
  const compareWord = range === 2 ? "last month" : `the last ${range - 1} months`;

  if (savedRounded === 0 && extraRounded === 0) return null;

  const isFlat = Math.abs(net) < 1;
  const isPositive = !isFlat && net > 0;
  const bg = isFlat ? "bg-[#F4F2EC]" : isPositive ? "bg-gradient-to-br from-[#E6F0E0] to-[#F4F6F1]" : "bg-gradient-to-br from-[#FCEDE6] to-[#FBF4EF]";
  const border = isFlat ? "border-[#E0DCD2]" : isPositive ? "border-[#C6D9BD]" : "border-[#F0C6B5]";
  const accent = isFlat ? "#5A5C58" : isPositive ? "#5F7161" : "#B8593E";
  const emoji = isFlat ? "🤔" : isPositive ? "💰" : "😬";
  const headline = isFlat
    ? "About even — same as before"
    : isPositive
    ? `You saved ${fmtCAD(net)} this month!`
    : `You spent ${fmtCAD(Math.abs(net))} more this month`;
  const sub = isFlat
    ? `Your variable spending is roughly the same as ${compareWord}.`
    : isPositive
    ? `Compared to your average over ${compareWord}, that's pocket money in the bank. 🎉`
    : `Compared to your average over ${compareWord}. Time to tighten up next month!`;

  return (
    <div
      data-testid="pocket-money-callout"
      className={`mt-6 rounded-2xl border ${border} ${bg} p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4`}
    >
      <div className="text-4xl shrink-0" aria-hidden="true">{emoji}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#5A5C58]">Pocket money saved this month</p>
        <p
          data-testid="pocket-money-amount"
          className="font-display text-2xl sm:text-3xl font-semibold mt-1"
          style={{ color: accent }}
        >
          {headline}
        </p>
        <p className="text-sm text-[#1A1C1A]/80 mt-1">{sub}</p>
      </div>
      <div className="flex flex-row sm:flex-col gap-3 sm:gap-1 text-xs font-mono shrink-0 self-stretch sm:self-center">
        <div className="flex-1 sm:flex-none px-3 py-2 rounded-lg bg-white/70 border border-[#D8D6D0] min-w-[120px] text-center">
          <p className="text-[10px] uppercase tracking-wider text-[#5A5C58] font-sans">Saved</p>
          <p className="text-[#5F7161] font-semibold mt-0.5">+{fmtCAD(savedRounded)}</p>
        </div>
        <div className="flex-1 sm:flex-none px-3 py-2 rounded-lg bg-white/70 border border-[#D8D6D0] min-w-[120px] text-center">
          <p className="text-[10px] uppercase tracking-wider text-[#5A5C58] font-sans">Overspent</p>
          <p className="text-[#B8593E] font-semibold mt-0.5">−{fmtCAD(extraRounded)}</p>
        </div>
      </div>
    </div>
  );
};
